<template>
  <v-dialog v-model="isOpen" max-width="820" scrollable>
    <v-card v-if="request">
      <v-card-title class="headline font-weight-light">
        {{ title }}
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <a @click="openAppointment" class="ml-2 text-decoration-none" v-on="on">
              #{{ request.appointment_code }}
              <v-icon class="ml-1" small>mdi-open-in-new</v-icon>
            </a>
          </template>
          <span>{{ $t('divergence.review.openAppointment') }}</span>
        </v-tooltip>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <v-row class="mb-2">
          <v-col cols="12" md="3">
            <p class="caption mb-0 grey--text">{{ $t('divergence.review.host') }}</p>
            <p class="body-2 mb-0">{{ request.host_name }}</p>
          </v-col>
          <v-col cols="12" md="3">
            <p class="caption mb-0 grey--text">{{ $t('divergence.review.realEstate') }}</p>
            <p class="body-2 mb-0">{{ request.real_estate_name }}</p>
          </v-col>
          <v-col cols="12" md="3">
            <p class="caption mb-0 grey--text">{{ $t('divergence.review.appointment') }}</p>
            <p class="body-2 mb-0">{{ formatDate(request.appointment_date) }}</p>
          </v-col>
          <v-col cols="12" md="3">
            <p class="caption mb-0 grey--text">{{ $t('divergence.review.checkIn') }}</p>

            <v-progress-circular
              v-if="isDivergenceCheckInLoading"
              class="mt-1"
              size="18"
              width="2"
              indeterminate
            />

            <p v-else-if="divergenceCheckInFailed" class="caption mb-0 warning--text">
              {{ $t('divergence.review.checkInFailed') }}
            </p>

            <template v-else>
              <p class="body-2 mb-0">{{ formatDate(checkInDate) }}</p>
              <a
                v-if="checkInMapUrl"
                :href="checkInMapUrl"
                class="caption text-decoration-none"
                target="_blank"
                rel="noopener"
              >
                <v-icon color="primary" x-small>mdi-map-marker</v-icon>
                {{ $t('divergence.review.checkInMap') }}
              </a>
              <p v-else class="caption mb-0 grey--text">
                {{ $t('divergence.review.noCheckInLocation') }}
              </p>
            </template>
          </v-col>
        </v-row>

        <v-alert v-if="isReimbursementRequest" class="mb-4" type="info" text dense>
          {{ $t('divergence.review.reimbursementExplainer') }}
        </v-alert>

        <v-card v-if="isReimbursementRequest && cancellation" class="mb-4" outlined>
          <v-card-text class="py-3">
            <v-row>
              <v-col cols="12" md="4" class="py-1">
                <p class="caption mb-0 grey--text">{{ $t('divergence.review.canceledAt') }}</p>
                <p class="body-2 mb-0">{{ formatDate(cancellation.canceled_at) }}</p>
              </v-col>
              <v-col cols="12" md="8" class="py-1">
                <p class="caption mb-0 grey--text">{{ $t('divergence.review.cancelReason') }}</p>
                <p class="body-2 mb-0">
                  {{ cancellation.cancel_reason || $t('divergence.review.noCancelReason') }}
                </p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- The app sends its single notes field for the whole submission, not per item. -->
        <template v-if="requestDescription">
          <p class="caption mb-0 grey--text">{{ $t('divergence.review.requestDescription') }}</p>
          <p class="body-2 mb-4">{{ requestDescription }}</p>
        </template>

        <p class="subtitle-2 mb-2">{{ itemsTitle }}</p>

        <v-card v-for="item in allItems" :key="item.id" class="mb-2" outlined>
          <v-card-text class="py-3">
            <div class="d-flex align-center mb-2">
              <v-checkbox
                v-if="isSelectable(request, item)"
                v-model="selectedIds"
                :value="item.id"
                :title="$t('divergence.review.selectItem')"
                class="mt-0 pt-0 mr-2"
                hide-details
                dense
              />
              <v-chip class="mr-2" small>{{ $t(`divergence.type.${item.type}`) }}</v-chip>
              <v-chip :class="statusColor(itemStatus(request, item))" class="mr-2" x-small>
                {{ $t(`divergence.status.${itemStatus(request, item)}`) }}
              </v-chip>
              <span v-if="item.realty_code" class="caption grey--text text-truncate">
                {{ item.realty_code }} — {{ item.realty_address }}
              </span>
              <v-tooltip v-if="distanceLabel(item)" bottom>
                <template v-slot:activator="{ on }">
                  <v-chip :class="distanceColor(item)" class="ml-2 flex-shrink-0" x-small v-on="on">
                    <v-icon class="mr-1" x-small>mdi-map-marker-distance</v-icon>
                    {{ $t('divergence.review.distanceFromRealty', { value: distanceLabel(item) }) }}
                  </v-chip>
                </template>
                <span>{{ distanceHint(item) }}</span>
              </v-tooltip>
            </div>

            <v-row class="mb-0">
              <v-col cols="6" class="py-1">
                <p class="caption mb-0 grey--text">{{ $t('divergence.review.registered') }}</p>
                <p class="body-2 mb-0">{{ registeredValue(item) }}</p>
              </v-col>
              <v-col cols="6" class="py-1">
                <p class="caption mb-0 grey--text">{{ $t('divergence.review.declared') }}</p>
                <p class="body-2 mb-0 font-weight-medium">{{ declaredValue(item) }}</p>
              </v-col>
            </v-row>

            <template v-if="item.approved_displacement_value != null">
              <p class="caption mb-0 mt-2 grey--text">
                {{ $t('divergence.review.approvedDisplacementValue') }}
              </p>
              <p class="body-2 mb-0 font-weight-medium">
                {{ formatMoney(item.approved_displacement_value) }}
              </p>
            </template>

            <template v-if="item.approved_reimbursement_value != null">
              <p class="caption mb-0 mt-2 grey--text">
                {{ $t('divergence.review.approvedReimbursementValue') }}
              </p>
              <p class="body-2 mb-0 font-weight-medium">
                {{ formatMoney(item.approved_reimbursement_value) }}
              </p>
            </template>

            <!-- Cobrado da imobiliária, o host recebe só a parte do contrato: mostra quanto foi. -->
            <template v-if="hasPartialHostShare(item)">
              <p class="caption mb-0 mt-2 grey--text">
                {{ $t('divergence.review.reimbursementHostReceived') }}
              </p>
              <p class="body-2 mb-0 font-weight-medium">
                {{ formatMoney(item.applied_host_delta) }}
                <span v-if="item.applied_host_percent != null" class="grey--text">
                  ({{ Number(item.applied_host_percent).toLocaleString() }}%)
                </span>
              </p>
            </template>

            <p class="caption mb-0 mt-2 grey--text">{{ $t('divergence.review.description') }}</p>
            <p class="body-2 mb-1">{{ item.description || $t('divergence.review.noDescription') }}</p>

            <template v-if="item.review_comment">
              <p class="caption mb-0 mt-2 grey--text">
                {{ $t('divergence.review.reviewCommentLabel') }}
              </p>
              <p class="body-2 mb-1">{{ item.review_comment }}</p>
            </template>

            <template v-if="item.contest_reason">
              <p class="caption mb-0 mt-2 grey--text">{{ $t('divergence.review.contestReasonLabel') }}</p>
              <p class="body-2 mb-1">{{ item.contest_reason }}</p>
            </template>

            <p class="caption mb-1 grey--text">{{ $t('divergence.review.evidence') }}</p>
            <v-progress-circular v-if="isDivergenceFilesLoading" size="20" width="2" indeterminate />
            <div v-else-if="hasEvidence(item)" class="d-flex flex-wrap gap-1">
              <v-tooltip v-for="fileId in item.file_ids" :key="fileId" bottom>
                <template v-slot:activator="{ on }">
                  <v-card
                    @click="openEvidence(fileId)"
                    class="mr-2 mb-1"
                    width="88"
                    height="88"
                    outlined
                    v-on="on"
                  >
                    <v-img
                      v-if="divergenceFileUrls[fileId]"
                      :src="divergenceFileUrls[fileId]"
                      width="88"
                      height="88"
                      class="clickable"
                    />
                    <div v-else class="d-flex align-center justify-center" style="height: 100%">
                      <v-icon color="grey lighten-1" small>mdi-image-off-outline</v-icon>
                    </div>
                  </v-card>
                </template>
                <span>
                  {{ divergenceFileUrls[fileId]
                    ? $t('divergence.review.openPhoto')
                    : $t('divergence.review.photoUnavailable') }}
                </span>
              </v-tooltip>
            </div>
            <p v-else class="body-2 mb-0 grey--text">{{ $t('divergence.review.noEvidence') }}</p>
          </v-card-text>
        </v-card>

        <v-alert v-if="!modalAction" class="mb-0" type="info" text dense>
          {{ $t('divergence.review.nothingToDo') }}
        </v-alert>

        <v-alert v-if="hasNoSelection" class="mb-0" type="warning" text dense>
          {{ $t('divergence.review.selectAtLeastOne') }}
        </v-alert>

        <v-alert v-else-if="isPartialSelection" class="mb-0" type="info" text dense>
          {{ $t('divergence.review.partialSelection') }}
        </v-alert>

        <template v-if="modalAction">
          <p v-if="modalAction !== 'reviewReimbursement'" class="subtitle-2 mb-2 mt-4">
            {{ deltaSectionTitle }}
          </p>

          <v-skeleton-loader v-if="isDivergenceSimulating" type="image" height="88" />

          <v-card
            v-else-if="shownDelta && modalAction !== 'reviewReimbursement'"
            class="mb-2"
            outlined
          >
            <v-card-text class="py-3">
              <v-row>
                <v-col cols="12" md="6" class="py-1">
                  <p class="caption mb-0 grey--text">{{ $t('divergence.review.hostDelta') }}</p>
                  <p class="title mb-0 success--text">{{ formatMoney(shownDelta.host_delta) }}</p>
                  <p v-if="modalAction === 'review'" class="caption mb-0 grey--text">
                    {{ formatMoney(shownDelta.previous_host_total) }} →
                    {{ formatMoney(shownDelta.new_host_total) }}
                  </p>
                </v-col>
                <v-col cols="12" md="6" class="py-1">
                  <p class="caption mb-0 grey--text">{{ $t('divergence.review.realEstateDelta') }}</p>
                  <p class="title mb-0">{{ formatMoney(shownDelta.real_estate_delta) }}</p>
                  <p v-if="modalAction === 'review'" class="caption mb-0 grey--text">
                    {{ formatMoney(shownDelta.previous_real_estate_total) }} →
                    {{ formatMoney(shownDelta.new_real_estate_total) }}
                  </p>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-alert v-if="hasNoHostShare" class="mb-2" type="info" text dense>
            {{ $t('divergence.review.noHostShare') }}
          </v-alert>

          <v-alert
            v-if="modalAction === 'review' && divergenceDelta && !divergenceDelta.full_recalculation_available"
            class="mb-0"
            type="warning"
            text
            dense
          >
            {{ $t('divergence.review.complementary') }}
          </v-alert>

          <!--
            A análise digita o valor do reembolso. Cobrando a imobiliária, ela paga esse valor e o
            host recebe a taxa do host, que abre com a taxa de deslocamento do contrato e pode ser
            trocada; sem cobrar, o host recebe o valor cheio.
          -->
          <div v-if="modalAction === 'reviewReimbursement'" class="d-flex align-center mt-4">
            <v-text-field
              v-model.lazy="approvedValue"
              v-money="catalogCurrency()"
              :label="$t('divergence.review.reimbursementValue')"
              style="max-width: 180px"
              outlined
              dense
              hide-details
            />
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="ml-2" color="grey lighten-1" small v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>{{ valueTooltip }}</span>
            </v-tooltip>
          </div>

          <v-checkbox
            v-if="modalAction === 'reviewReimbursement'"
            v-model="chargeRealEstate"
            :label="$t('divergence.review.chargeRealEstate')"
            :hint="$t('divergence.review.chargeRealEstateHint')"
            class="mt-3"
            persistent-hint
            dense
          />

          <!--
            Lida como no contrato: 90 paga 90% ao host e 0 não paga nada. É o oposto do avulso, em
            que 0 paga o valor cheio — por isso a dica repete o que o 0 faz aqui.
          -->
          <v-text-field
            v-if="modalAction === 'reviewReimbursement' && chargeRealEstate"
            v-model="hostPercent"
            :label="$t('divergence.review.reimbursementHostPercent')"
            :hint="$t('divergence.review.reimbursementHostPercentHint')"
            :error-messages="isHostPercentValid ? [] : [$t('divergence.review.reimbursementHostPercentInvalid')]"
            type="number"
            min="0"
            max="100"
            step="0.01"
            suffix="%"
            class="mt-3"
            style="max-width: 360px"
            persistent-hint
            outlined
            dense
          />

          <p
            v-if="modalAction === 'reviewReimbursement' && reimbursementPreview"
            class="body-2 font-weight-medium mt-3 mb-0"
          >
            {{ reimbursementPreview }}
          </p>

          <!--
            What the host declared is only a suggestion: the displacement is recalculated from the
            amount typed here, split by the contract's displacement rate.
          -->
          <div v-if="hasSelectedDisplacement" class="d-flex align-center mt-4">
            <v-text-field
              v-model.lazy="displacementValue"
              v-money="catalogCurrency()"
              :label="$t('divergence.review.displacementValue')"
              style="max-width: 180px"
              outlined
              dense
              hide-details
            />
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon class="ml-2" color="grey lighten-1" small v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>{{ displacementTooltip }}</span>
            </v-tooltip>
          </div>

          <v-textarea
            v-if="modalAction === 'review' || modalAction === 'reviewReimbursement'"
            v-model="reviewComment"
            class="mt-4"
            :label="$t('divergence.review.reviewComment')"
            :hint="$t('divergence.review.reviewCommentHint')"
            persistent-hint
            rows="2"
            counter="512"
            maxlength="512"
            outlined
            dense
          />

          <v-textarea
            v-if="modalAction === 'contest'"
            v-model="contestReason"
            class="mt-4"
            :label="$t('divergence.review.contestReason')"
            :hint="$t('divergence.review.contestReasonHint')"
            rows="2"
            counter="512"
            maxlength="512"
            outlined
            dense
            persistent-hint
          />
        </template>
      </v-card-text>

      <v-divider />

      <v-card-actions v-if="!pendingAction">
        <v-btn @click="close" text>{{ $t('divergence.actions.close') }}</v-btn>
        <v-spacer />

        <template v-if="modalAction === 'reviewReimbursement'">
          <v-btn @click="pendingAction = 'refuse'" color="error" text>
            {{ $t('divergence.actions.refuse') }}
          </v-btn>
          <v-btn
            @click="pendingAction = 'approveReimbursement'"
            :disabled="!isApprovedValueValid || !isHostPercentValid"
            color="primary"
            depressed
          >
            {{ $t('divergence.actions.approveReimbursement') }}
          </v-btn>
        </template>

        <template v-else-if="modalAction === 'review'">
          <v-btn @click="pendingAction = 'refuse'" :disabled="hasNoSelection" color="error" text>
            {{ $t('divergence.actions.refuse') }}
          </v-btn>
          <v-btn
            @click="pendingAction = 'approve'"
            :disabled="hasNoSelection || isDivergenceSimulating || !divergenceDelta || isMissingDisplacementValue"
            color="primary"
            depressed
          >
            {{ $t('divergence.actions.recalculate') }}
          </v-btn>
        </template>

        <v-btn
          v-else-if="modalAction === 'contest'"
          @click="pendingAction = 'contest'"
          :disabled="!isContestReasonValid"
          color="warning"
          depressed
        >
          {{ $t('divergence.actions.contest') }}
        </v-btn>

        <v-btn
          v-else-if="modalAction === 'reverse'"
          @click="pendingAction = 'reverse'"
          color="error"
          depressed
        >
          {{ $t('divergence.actions.reverse') }}
        </v-btn>
      </v-card-actions>

      <v-card-actions v-else class="flex-column align-stretch">
        <v-alert class="mb-3" :type="confirmationSeverity" text dense>
          {{ confirmationText }}
        </v-alert>
        <div class="d-flex">
          <v-btn @click="pendingAction = null" :disabled="isDivergenceReviewing" text>
            {{ $t('divergence.actions.back') }}
          </v-btn>
          <v-spacer />
          <v-btn
            @click="executePendingAction"
            :loading="isDivergenceReviewing"
            :color="confirmationSeverity === 'info' ? 'primary' : 'error'"
            depressed
          >
            {{ $t('divergence.actions.confirm') }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { VMoney } from 'v-money'
import { catalogCurrency, catalogDate, catalogMonetary } from '@/util/catalog'
import { clearCurrency } from '@/util/helpers'
import { language } from '@/util/language'
import {
  actionableItemIds,
  appliedDelta,
  checkInDistance,
  checkInMapUrl,
  collectEvidencePairs,
  formatDistance,
  initialDisplacementValue,
  isCheckInFar,
  isReimbursement,
  isSelectable,
  isValidHostPercent,
  itemStatus,
  parseHostPercent,
  reimbursementDelta,
  reimbursementHostValue,
  resolveModalAction,
  resolveReimbursementAction,
  statusColor,
} from '../divergenceHelpers'

const MIN_CONTEST_REASON = 10

/** As ações que somam valor para o host. As outras tiram, e a confirmação avisa em tom de alerta. */
const APPROVAL_ACTIONS = ['approve', 'approveReimbursement']

export default {
  name: 'ModalReviewDivergence',

  directives: {
    money: VMoney,
  },

  data() {
    return {
      isOpen: false,
      request: null,
      reviewComment: '',
      contestReason: '',
      approvedValue: 0,
      /** Trip reimbursement only: whether the real estate is debited the approved amount too. */
      chargeRealEstate: false,
      /** Cobrando a imobiliária: a parte do host em %, aberta com a taxa do contrato. */
      hostPercent: null,
      displacementValue: 0,
      pendingAction: null,
      /** Itens marcados para a decisão atual. Fora da análise, a ação sempre é do lote inteiro. */
      selectedIds: [],
      formatDate: catalogDate,
      formatMoney: catalogMonetary,
      catalogCurrency,
    }
  },

  computed: {
    ...mapGetters([
      'divergenceDelta',
      'divergenceFileUrls',
      'divergenceCheckIn',
      'isDivergenceCheckInLoading',
      'divergenceCheckInFailed',
      'isDivergenceSimulating',
      'isDivergenceReviewing',
      'isDivergenceFilesLoading',
    ]),

    checkInDate() {
      return this.divergenceCheckIn && this.divergenceCheckIn.date
    },

    /** Onde o host bateu o checkin. Nulo fora do fluxo de checklist, que não grava coordenada. */
    checkInMapUrl() {
      return checkInMapUrl(this.divergenceCheckIn)
    },

    /** Mostra o pedido inteiro, não só o acionável: o resolvido é o contexto da decisão. */
    allItems() {
      return (this.request && this.request.items) || []
    },

    isReimbursementRequest() {
      return isReimbursement(this.request)
    },

    /** O pedido de ressarcimento fala de uma ida ao imóvel; o de recálculo, do serviço cobrado. */
    title() {
      return this.isReimbursementRequest
        ? this.$t('divergence.review.reimbursementTitle')
        : this.$t('divergence.review.title')
    },

    /**
     * Shared by every item of the submission, so the first one that has it is enough. Hidden when
     * an item already shows the same text — the reimbursement stores it on both.
     */
    requestDescription() {
      const items = (this.request && this.request.items) || []
      const found = items.find((item) => item.request_description)
      if (!found) return null
      const repeated = items.some((item) => item.description === found.request_description)
      return repeated ? null : found.request_description
    },

    itemsTitle() {
      return this.isReimbursementRequest
        ? this.$t('divergence.review.reimbursementItems')
        : this.$t('divergence.review.items')
    },

    /**
     * 'review' | 'reviewReimbursement' | 'contest' | 'reverse' | null. Um pedido nunca aciona dois
     * status ao mesmo tempo, e o ressarcimento nunca cai nos caminhos de contestação: a
     * imobiliária não é debitada por ele.
     */
    modalAction() {
      if (this.isReimbursementRequest) return resolveReimbursementAction(this.request)
      return resolveModalAction(this.request)
    },

    actionableIds() {
      return actionableItemIds(this.request)
    },

    /** Ids que a ação vai levar: a seleção na análise, o lote inteiro em contestação e estorno. */
    decisionIds() {
      return this.modalAction === 'review' ? this.selectedIds : this.actionableIds
    },

    hasNoSelection() {
      return this.modalAction === 'review' && this.selectedIds.length === 0
    },

    isPartialSelection() {
      return this.modalAction === 'review'
        && this.selectedIds.length > 0
        && this.selectedIds.length < this.actionableIds.length
    },

    /** Pendente simula; resolvido mostra o que já foi lançado. */
    shownDelta() {
      if (this.isReimbursementRequest) return reimbursementDelta(this.request)
      return this.modalAction === 'review' ? this.divergenceDelta : appliedDelta(this.request)
    },

    /** O cancelamento que originou a ida, para o analista julgar o pedido. */
    cancellation() {
      const item = this.allItems.find((current) => current.cancellation_history_id)
      return item || null
    },

    hasNoHostShare() {
      // no ressarcimento quem define o valor do host é a análise, não a taxa do contrato
      if (this.isReimbursementRequest || !this.shownDelta) return false
      return Number(this.shownDelta.host_delta) === 0
        && Number(this.shownDelta.real_estate_delta) > 0
    },

    deltaSectionTitle() {
      if (this.modalAction === 'review') return this.$t('divergence.review.delta')
      return this.$t('divergence.review.appliedDelta')
    },

    isContestReasonValid() {
      return this.contestReason.trim().length >= MIN_CONTEST_REASON
    },

    /** O que vai no corpo da aprovação: o texto mascarado vira número aqui, num lugar só. */
    /** The pending displacement among the checked items; a request carries at most one. */
    selectedDisplacement() {
      if (this.modalAction !== 'review' || !this.request) return null
      return this.allItems.find(
        (item) => item.type === 'DISPLACEMENT' && this.selectedIds.includes(item.id),
      ) || null
    },

    hasSelectedDisplacement() {
      return this.selectedDisplacement != null
    },

    displacementValueNumber() {
      return Number(clearCurrency(this.displacementValue)) || 0
    },

    isMissingDisplacementValue() {
      return this.hasSelectedDisplacement && this.displacementValueNumber <= 0
    },

    displacementTooltip() {
      // o app atual não manda valor: sem sugestão do host, a dica explica quem digita e como divide
      if (this.selectedDisplacement?.declared_displacement_value == null) {
        return this.$t('divergence.review.displacementValueHint')
      }
      return this.$t('divergence.review.displacementValueSuggested', {
        value: this.formatMoney(this.selectedDisplacement?.declared_displacement_value),
      })
    },

    approvedDisplacementValue() {
      return this.hasSelectedDisplacement ? this.displacementValueNumber : undefined
    },

    approvedValueNumber() {
      return Number(clearCurrency(this.approvedValue)) || 0
    },

    isApprovedValueValid() {
      return this.approvedValueNumber > 0
    },

    /** A taxa de deslocamento do contrato; nula quando o backend não conseguiu ler o contrato. */
    contractHostPercent() {
      return this.request?.reimbursement_host_percent ?? null
    },

    /** A taxa do host digitada; vazia vira nula e trava a aprovação cobrando a imobiliária. */
    hostPercentNumber() {
      return parseHostPercent(this.hostPercent)
    },

    isHostPercentValid() {
      return !this.chargeRealEstate || isValidHostPercent(this.hostPercentNumber)
    },

    /** Cobrando a imobiliária, a taxa do host; sem cobrar, o valor cheio. */
    hostReimbursementValue() {
      if (!this.chargeRealEstate) return this.approvedValueNumber
      if (!this.isHostPercentValid) return null
      return reimbursementHostValue(this.approvedValueNumber, this.hostPercentNumber)
    },

    valueTooltip() {
      if (this.contractHostPercent == null) {
        return this.$t('divergence.review.reimbursementContractShareUnknown')
      }
      if (Number(this.contractHostPercent) === 0) {
        return this.$t('divergence.review.reimbursementContractNoShare')
      }
      return this.$t('divergence.review.reimbursementContractShare', {
        percent: Number(this.contractHostPercent).toLocaleString(),
      })
    },

    /** O que cada lado paga e recebe com o valor digitado, antes de confirmar. */
    reimbursementPreview() {
      if (!this.isApprovedValueValid) return null
      const value = this.formatMoney(this.approvedValueNumber)
      if (!this.chargeRealEstate) return this.$t('divergence.review.reimbursementPreviewFull', { value })
      if (this.hostReimbursementValue == null) return null
      return this.$t('divergence.review.reimbursementPreviewCharging', {
        value,
        hostValue: this.formatMoney(this.hostReimbursementValue),
      })
    },

    confirmationSeverity() {
      return APPROVAL_ACTIONS.includes(this.pendingAction) ? 'info' : 'warning'
    },

    /** Repete o valor no momento da confirmação: é o número que vai ser lançado. */
    confirmationText() {
      if (this.pendingAction === 'approveReimbursement') {
        // sem a taxa do contrato não há como dizer quanto o host recebe: cai no texto sem o número
        if (this.chargeRealEstate && this.hostReimbursementValue == null) {
          return this.$t('divergence.review.confirmReimbursement')
        }
        const key = this.chargeRealEstate ? 'confirmReimbursementCharging' : 'confirmReimbursementWithValue'
        return this.$t(`divergence.review.${key}`, {
          value: this.formatMoney(this.approvedValueNumber),
          hostValue: this.formatMoney(this.hostReimbursementValue),
        })
      }
      if (this.pendingAction === 'refuse') return this.$t('divergence.review.confirmRefuse')
      if (this.pendingAction === 'contest') return this.$t('divergence.review.confirmContest')
      if (this.pendingAction === 'reverse') {
        const delta = appliedDelta(this.request)
        if (!delta) return this.$t('divergence.review.confirmReverse')
        return this.$t('divergence.review.confirmReverseWithValues', {
          host: this.formatMoney(delta.host_delta),
          realEstate: this.formatMoney(delta.real_estate_delta),
        })
      }
      if (!this.divergenceDelta) return this.$t('divergence.review.confirmRecalculate')
      return this.$t('divergence.review.confirmRecalculateWithValues', {
        host: this.formatMoney(this.divergenceDelta.host_delta),
        realEstate: this.formatMoney(this.divergenceDelta.real_estate_delta),
      })
    },
  },

  watch: {
    /** A prévia tem de refletir só o que vai ser aprovado, senão o analista confirma outro número. */
    selectedIds() {
      this.simulateSelection()
    },

    /** Typing a new amount changes the recalculation, so the preview follows it. */
    displacementValue() {
      this.simulateSelection()
    },
  },

  methods: {
    ...mapActions([
      'fetchDivergenceCheckIn',
      'simulateDivergences',
      'approveDivergences',
      'approveReimbursements',
      'refuseDivergences',
      'contestDivergences',
      'reverseDivergences',
      'fetchDivergenceFileUrls',
    ]),
    ...mapMutations(['setDivergenceDelta', 'setDivergenceFileUrls', 'setDivergenceCheckIn']),

    statusColor,

    isSelectable,
    itemStatus,

    openModal(request) {
      this.request = request
      this.reviewComment = ''
      this.contestReason = ''
      this.approvedValue = 0
      this.chargeRealEstate = false
      this.hostPercent = request?.reimbursement_host_percent ?? null
      this.displacementValue = initialDisplacementValue(request)
      this.pendingAction = null
      this.setDivergenceDelta(null)
      this.isOpen = true

      // simular só faz sentido em item pendente; resolvido já tem valor gravado. A atribuição
      // dispara o watcher, que simula com a seleção — por isso não simula aqui também. O
      // ressarcimento fica de fora: ele decide o pedido inteiro, e o simulate devolveria o delta
      // do serviço, que é outra conta
      this.selectedIds = !isReimbursement(request) && resolveModalAction(request) === 'review'
        ? actionableItemIds(request)
        : []
      this.fetchDivergenceFileUrls(collectEvidencePairs(request))
      this.fetchDivergenceCheckIn(request.appointment_id)
    },

    /** The preview has to reflect only what will be approved, or the reviewer confirms another number. */
    simulateSelection() {
      if (this.modalAction !== 'review') return
      if (!this.selectedIds.length) {
        this.setDivergenceDelta(null)
        return
      }
      if (this.isMissingDisplacementValue) {
        this.setDivergenceDelta(null)
        return
      }
      this.simulateDivergences({
        divergenceIds: this.selectedIds,
        approvedDisplacementValue: this.approvedDisplacementValue,
      })
    },

    close() {
      this.isOpen = false
      this.request = null
      this.pendingAction = null
      this.selectedIds = []
      this.setDivergenceDelta(null)
      this.setDivergenceFileUrls({})
      this.setDivergenceCheckIn(null)
    },

    async executePendingAction() {
      const action = this.pendingAction
      const done = await this.runAction(action)
      if (!done) {
        this.pendingAction = null
        return
      }
      this.$store.commit('setToasterMessage', this.$t(`divergence.messages.${action}`))
      this.close()
    },

    runAction(action) {
      const divergenceIds = this.decisionIds
      if (action === 'approveReimbursement') {
        return this.approveReimbursements({
          divergenceIds,
          reviewComment: this.reviewComment,
          approvedValue: this.approvedValueNumber,
          chargeRealEstate: this.chargeRealEstate,
          hostPercent: this.chargeRealEstate ? this.hostPercentNumber : null,
        })
      }
      if (action === 'approve') {
        return this.approveDivergences({
          divergenceIds,
          reviewComment: this.reviewComment,
          approvedDisplacementValue: this.approvedDisplacementValue,
        })
      }
      if (action === 'refuse') {
        return this.refuseDivergences({ divergenceIds, reviewComment: this.reviewComment })
      }
      if (action === 'contest') {
        return this.contestDivergences({ divergenceIds, contestReason: this.contestReason })
      }
      return this.reverseDivergences({ divergenceIds })
    },

    /** Sem coordenada dos dois lados não há distância, e aí a tela não mostra nada. */
    distanceLabel(item) {
      return formatDistance(this.distanceOf(item), language())
    },

    distanceColor(item) {
      return isCheckInFar(this.distanceOf(item)) ? 'orange lighten-4' : 'grey lighten-3'
    },

    distanceHint(item) {
      return isCheckInFar(this.distanceOf(item))
        ? this.$t('divergence.review.distanceFarHint')
        : this.$t('divergence.review.distanceHint')
    },

    distanceOf(item) {
      return checkInDistance(this.divergenceCheckIn, item, this.request)
    },

    hasEvidence(item) {
      return (item.file_ids || []).length > 0
    },

    openEvidence(fileId) {
      const url = this.divergenceFileUrls[fileId]
      if (url) window.open(url, '_blank')
    },

    /** Nova aba: o card fica aberto atrás para o analista voltar e decidir. */
    openAppointment() {
      const url = this.$router.resolve({
        name: 'adminAppointmentsDetails',
        params: { id: this.request.appointment_id },
      }).href
      window.open(url, '_blank')
    },

    registeredValue(item) {
      if (item.type === 'AREA') {
        return this.$t('divergence.review.squareMeters', { value: item.previous_area_meters || '—' })
      }
      // o ressarcimento não corrige cadastro nenhum: não há valor anterior a comparar
      if (item.type === 'TRIP_REIMBURSEMENT') return this.$t('divergence.review.notApplicable')
      return this.$t('divergence.review.notInRegistry')
    },

    hasPartialHostShare(item) {
      return item.approved_reimbursement_value != null
        && item.applied_host_delta != null
        && Number(item.applied_host_delta) !== Number(item.approved_reimbursement_value)
    },

    declaredValue(item) {
      if (item.type === 'AREA') {
        return this.$t('divergence.review.squareMeters', { value: item.declared_area_meters })
      }
      if (item.type === 'DISPLACEMENT' || item.type === 'TRIP_REIMBURSEMENT') {
        // o app atual não manda valor: o número é da análise, não do host
        if (item.declared_displacement_value == null) return this.$t('divergence.review.notDeclared')
        return this.formatMoney(item.declared_displacement_value)
      }
      return this.$t('divergence.review.hasFeature')
    },
  },
}
</script>
